// Set Piece Goals — types + shaping (Phase 3, half-donut type).
//
// Two half-donuts for one team (chosen by the `.team-toggle`): "Scored" (green)
// and "Conceded" (red). Each donut's single arc = the % of the team's goals
// scored/conceded from set pieces in the window, with the centre % text shown.
// Pure + framework-free; the arc geometry + D3 animation live in <HalfDonut>.

import type { DonutSegment } from "./donut.js";

type Window = "5" | "10" | "Season";

/** Set-piece share (percent) for one window. */
export interface SetPieceWindow {
  scored: number;
  conceded: number;
}

/** Set Piece data keyed by full team name, then window. */
export type SetPieceData = Record<string, Record<Window, SetPieceWindow>>;

/** The two donuts, in display order. */
export type SetPieceSlot = "Scored" | "Conceded";
export const SET_PIECE_SLOTS: readonly SetPieceSlot[] = [
  "Scored",
  "Conceded",
] as const;

/** A shaped donut: its arcs + the centre % text (always shown for Set Piece). */
export interface SetPieceDonut {
  slot: SetPieceSlot;
  segments: DonutSegment[];
  pct: { value: number; color: string };
}

/** Normalise the timeline control to a data key (anything but 10/Season → 5). */
function toWindow(timeline: string | null | undefined): Window {
  return timeline === "10" ? "10" : timeline === "Season" ? "Season" : "5";
}

const GREEN = "#2ecc71";
const GREY = "#888";
const RED = "#e74c3c";

/**
 * Shape one team's two Set Piece donuts (Scored, Conceded) for the window.
 * `team` may be undefined (missing data) — falls back to 0/0.
 */
export function shapeSetPiece(
  team: Record<Window, SetPieceWindow> | undefined,
  timeline: string | null | undefined
): SetPieceDonut[] {
  const d = team?.[toWindow(timeline)] ?? { scored: 0, conceded: 0 };
  return SET_PIECE_SLOTS.map((slot) => {
    const pct = slot === "Scored" ? d.scored : d.conceded;
    const color = slot === "Conceded" ? RED : GREEN;
    return {
      slot,
      segments: [
        { value: pct, color, key: "SP" },
        { value: 100 - pct, color: GREY, key: "OP" },
      ] as DonutSegment[],
      pct: { value: pct, color },
    };
  });
}
